import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Barcode,
  Calculator,
  CreditCard,
  Link2,
  Plus,
  QrCode,
  Search,
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { BotaoFlutuante } from '@/components/layout/BotaoFlutuante';
import {
  GradeNumeros,
  IconeQuadrado,
  ItemLista,
  ListaMobile,
  Selo,
} from '@/components/common/ListaMobile';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useCobrancas, useConfigPagamentos } from '@/hooks/useCobrancas';
import type { MetodoCobranca, StatusCobranca } from '@/api/endpoints/cobrancas';
import { DetalheCobranca } from './DetalheCobranca';
import { NovaCobrancaModal, type ProdutorEscolhido } from './NovaCobrancaModal';
import { SimuladorParcelasModal } from './SimuladorParcelasModal';
import {
  ehLink,
  rotuloForma,
  STATUS_INFO,
  dataCurta,
  dataHora,
  descricaoDaCobranca,
  nomeDoCliente,
  reaisDeCentavos,
} from './formato';

const LIMITE = 30;

const FILTROS: { valor: StatusCobranca | undefined; rotulo: string }[] = [
  { valor: undefined, rotulo: 'Todas' },
  { valor: 'PENDENTE', rotulo: 'Aguardando' },
  { valor: 'PAGA', rotulo: 'Pagas' },
  { valor: 'EXPIRADA', rotulo: 'Expiradas' },
  { valor: 'REEMBOLSADA', rotulo: 'Reembolsadas' },
];

const ICONE_METODO: Record<MetodoCobranca, typeof QrCode> = {
  PIX: QrCode,
  BOLETO: Barcode,
  CARTAO: CreditCard,
};

const COR_METODO: Record<MetodoCobranca, string> = {
  PIX: 'bg-success/15 text-success',
  BOLETO: 'bg-primary/15 text-primary',
  CARTAO: 'bg-tertiary/15 text-tertiary',
};

export function Cobrancas() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusCobranca | undefined>();
  const [texto, setTexto] = useState('');
  const [busca, setBusca] = useState('');
  const [page, setPage] = useState(1);
  const [novaAberta, setNovaAberta] = useState(false);
  const [produtorInicial, setProdutorInicial] = useState<ProdutorEscolhido | null>(null);
  const [aberta, setAberta] = useState<number | null>(null);
  const [simuladorAberto, setSimuladorAberto] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setBusca(texto.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [texto]);

  // Vindo do "Cobrar" no detalhe do usuário: abre o formulário com ele.
  useEffect(() => {
    const produtor = (location.state as { cobrarProdutor?: ProdutorEscolhido } | null)
      ?.cobrarProdutor;
    if (produtor) {
      setProdutorInicial(produtor);
      setNovaAberta(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const { data: config } = useConfigPagamentos();
  const { data, isLoading, isError } = useCobrancas({
    status,
    busca: busca || undefined,
    page,
    limit: LIMITE,
  });

  const resumo = (s: StatusCobranca) => data?.resumo.find((r) => r.status === s);
  const pendentes = resumo('PENDENTE');
  const pagas = resumo('PAGA');
  const cobrancas = data?.cobrancas ?? [];
  const totalPaginas = data ? Math.max(1, Math.ceil(data.total / LIMITE)) : 1;
  const naoConfigurado = config && !config.configurado;

  const abrirNova = () => {
    setProdutorInicial(null);
    setNovaAberta(true);
  };

  return (
    <Layout breadcrumbs={[{ label: 'Finanças', path: '/financas' }, { label: 'Cobranças' }]}>
      <div className="flex flex-col gap-4 p-3 pb-24 sm:p-4 md:pb-4 lg:gap-6 lg:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="max-md:hidden text-2xl font-bold tracking-tight text-text-title sm:text-3xl">
              Cobranças
            </h1>
            <p className="text-sm text-text-secondary">
              PIX, boleto e cartão pela AbacatePay. Plano pago é ativado sozinho.
            </p>
          </div>
          <div className="flex gap-2 max-md:w-full">
            <Button
              variant="secondary"
              onClick={() => setSimuladorAberto(true)}
              className="max-md:flex-1"
            >
              <Calculator size={16} />
              Simular parcelas
            </Button>
            <Button onClick={abrirNova} disabled={naoConfigurado} className="max-md:hidden">
              <Plus size={16} />
              Nova cobrança
            </Button>
          </div>
        </div>

        {naoConfigurado && (
          <div className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4 text-sm">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-warning" />
            <p className="text-text-body">
              Pagamentos ainda não configurados na API. Falta a chave da AbacatePay no ambiente.
            </p>
          </div>
        )}

        <GradeNumeros
          colunas={2}
          itens={[
            {
              rotulo: `Aguardando pagamento · ${pendentes?.quantidade ?? 0}`,
              valor: reaisDeCentavos(pendentes?.valorCentavos ?? 0),
              cor: 'text-warning',
            },
            {
              rotulo: `Pagas · ${pagas?.quantidade ?? 0}`,
              valor: reaisDeCentavos(pagas?.valorCentavos ?? 0),
              cor: 'text-success',
            },
          ]}
          className="lg:max-w-xl"
        />

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div
            className="-mx-3 flex gap-2 overflow-x-auto px-3 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden"
            role="group"
            aria-label="Status"
          >
            {FILTROS.map((f) => {
              const ativo = status === f.valor;
              return (
                <button
                  key={f.rotulo}
                  type="button"
                  aria-pressed={ativo}
                  onClick={() => {
                    setStatus(f.valor);
                    setPage(1);
                  }}
                  className={cn(
                    'shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
                    ativo
                      ? 'border-primary bg-primary text-bg-main'
                      : 'border-border bg-card text-text-secondary hover:bg-muted'
                  )}
                >
                  {f.rotulo}
                </button>
              );
            })}
          </div>
          <div className="relative lg:w-72">
            <Search
              size={16}
              className="absolute left-3 top-2.5 text-text-hint"
              aria-hidden="true"
            />
            <input
              className="w-full rounded-lg border border-border bg-card py-2 pl-9 pr-3 text-sm text-text-title outline-none placeholder:text-text-hint focus:border-primary"
              placeholder="Buscar cliente ou descrição"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
          </div>
        </div>

        {isError && (
          <p className="rounded-xl border border-error/30 bg-error/5 p-4 text-sm text-error">
            Não deu para carregar as cobranças.
          </p>
        )}

        {!isLoading && !isError && cobrancas.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border bg-card px-6 py-12 text-center">
            <p className="font-semibold text-text-title">Nenhuma cobrança por aqui</p>
            <p className="mt-1 text-sm text-text-secondary">
              Gere um PIX, boleto ou link de cartão em "Nova cobrança".
            </p>
          </div>
        )}

        {cobrancas.length > 0 && (
          <>
            <ListaMobile className="lg:hidden">
              {cobrancas.map((c) => {
                const Icone = ehLink(c) ? Link2 : ICONE_METODO[c.metodo ?? 'CARTAO'];
                return (
                  <ItemLista
                    key={c.id}
                    onClick={() => setAberta(c.id)}
                    inicio={
                      <IconeQuadrado
                        className={
                          ehLink(c)
                            ? 'bg-primary/15 text-primary'
                            : COR_METODO[c.metodo ?? 'CARTAO']
                        }
                      >
                        <Icone size={18} />
                      </IconeQuadrado>
                    }
                    titulo={nomeDoCliente(c)}
                    apoio={descricaoDaCobranca(c)}
                    meta={`${rotuloForma(c)} · ${dataCurta(c.created_at)}`}
                    fim={
                      <>
                        <span className="text-sm font-bold tabular-nums text-text-title">
                          {reaisDeCentavos(c.valor_centavos)}
                        </span>
                        <Selo tom={STATUS_INFO[c.status].tom}>{STATUS_INFO[c.status].rotulo}</Selo>
                      </>
                    }
                  />
                );
              })}
            </ListaMobile>

            <div className="hidden overflow-hidden rounded-xl border border-border bg-card shadow-card lg:block">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-text-secondary">
                  <tr>
                    <th className="px-4 py-3 font-medium">Criada</th>
                    <th className="px-4 py-3 font-medium">Cliente</th>
                    <th className="px-4 py-3 font-medium">Cobrança</th>
                    <th className="px-4 py-3 font-medium">Forma</th>
                    <th className="px-4 py-3 text-right font-medium">Valor</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {cobrancas.map((c) => {
                    const Icone = ehLink(c) ? Link2 : ICONE_METODO[c.metodo ?? 'CARTAO'];
                    return (
                      <tr
                        key={c.id}
                        onClick={() => setAberta(c.id)}
                        className="cursor-pointer transition-colors hover:bg-muted/40"
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
                          {dataHora(c.created_at)}
                        </td>
                        <td className="px-4 py-3 font-medium text-text-title">
                          {nomeDoCliente(c)}
                        </td>
                        <td className="px-4 py-3 text-text-body">{descricaoDaCobranca(c)}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5 text-text-body">
                            <Icone size={15} className="text-text-secondary" />
                            {rotuloForma(c)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums text-text-title">
                          {reaisDeCentavos(c.valor_centavos)}
                        </td>
                        <td className="px-4 py-3">
                          <Selo tom={STATUS_INFO[c.status].tom}>
                            {STATUS_INFO[c.status].rotulo}
                          </Selo>
                          {c.modo_teste && (
                            <Selo tom="destaque" className="ml-1.5">
                              Teste
                            </Selo>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPaginas > 1 && (
              <div className="flex items-center justify-center gap-3 text-sm">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Anterior
                </Button>
                <span className="text-text-secondary">
                  {page} de {totalPaginas}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= totalPaginas}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {!naoConfigurado && <BotaoFlutuante icon={Plus} label="Nova cobrança" onClick={abrirNova} />}

      <NovaCobrancaModal
        aberto={novaAberta}
        produtorInicial={produtorInicial}
        onClose={() => setNovaAberta(false)}
        onCriada={(id) => {
          setNovaAberta(false);
          setAberta(id);
        }}
      />
      <DetalheCobranca id={aberta} onClose={() => setAberta(null)} />
      <SimuladorParcelasModal aberto={simuladorAberto} onClose={() => setSimuladorAberto(false)} />
    </Layout>
  );
}
